import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// TRACK B, 2026-10-25 -- the TA synthesis/umbrella article. SYNTHESIS-ONLY
// per the approved scope, plus one narrow gap-fill: a decile cut of the
// already-blessed rsi_14/stoch_slowk_14_3 (reusing pct_52wh_decile_chart.py's
// exact machinery, generalized -- backend/rsi_decile_chart.py). No new
// sweep, no MACD-crossover or golden/death-cross event study (both
// explicitly flagged as untested, not silently skipped).
//
// Flipped live 2026-10-25, dispatched from MarketMythsPage.tsx's
// TEMPLATED_EVIDENCE map at /market-myths?evidence=ta-synthesis -- same
// embeddable-component shape as the other evidence docs. The RSI/
// Stochastic pooled-vs-per-date reconciliation (methodology note + both
// figures shown explicitly) was added and reviewed before this flip --
// see git history for the pre-reconciliation draft. Every number sourced
// to the live feature_library table (pulled directly, 2026-10-25) or to
// data/rsi_decile_means.json (backend/rsi_decile_chart.py, same run).

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=ta-synthesis";

// RSI(14) / Stochastic %K(14,3) decile cut -- per-date cross-sectional
// bucketing of an ALREADY-BLESSED continuous factor, H=5, same machinery
// as the 52wk-high/momentum-5d decile charts. Decile 0 = lowest reading
// (oversold end), decile 9 = highest (overbought end).
const RSI_DECILE: number[] = [0.003257, 0.003553, 0.00285, 0.002133, 0.001224, 0.000706, -0.000137, -0.001004, -0.001518, -0.000867];
const RSI_MONO = -0.9515;
const RSI_N_DATES = 2467;
const STOCH_DECILE: number[] = [0.002005, 0.003161, 0.002832, 0.002128, 0.001507, 0.0008, 0.000329, -0.000622, -0.001803, -0.000119];
const STOCH_MONO = -0.8909;
const STOCH_N_DATES = 2464;

function DecileChart({ decile, nDates, mono, label, ariaLabel }: {
  decile: number[]; nDates: number; mono: number; label: string; ariaLabel: string;
}) {
  const width = 680;
  const rowH = 24;
  const topPad = 10;
  const height = decile.length * rowH + topPad + 20;
  const labelW = 70;
  const plotW = width - labelW - 90;
  const midX = labelW + plotW / 2;
  const maxAbs = Math.max(...decile.map((v) => Math.abs(v)));
  const scale = (plotW / 2 - 10) / maxAbs;

  return (
    <figure className="hatpl-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label={ariaLabel}>
        <line x1={midX} y1={4} x2={midX} y2={height - 16} className="hatpl-chart-zeroline" />
        {decile.map((v, i) => {
          const y = topPad + i * rowH;
          const w = Math.abs(v) * scale;
          const x = v >= 0 ? midX : midX - w;
          const dLabel = i === 0 ? "D1 (lowest / oversold)" : i === 9 ? "D10 (highest / overbought)" : `D${i + 1}`;
          return (
            <g key={i}>
              <text x={0} y={y + 12} className="hatpl-chart-label">{dLabel}</text>
              <rect x={x} y={y + 2} width={Math.max(w, 1)} height={14} className={v >= 0 ? "hatpl-chart-bar" : "hatpl-chart-bar-neg"} />
              <text x={v >= 0 ? x + w + 6 : x - 6} y={y + 12}
                    textAnchor={v >= 0 ? "start" : "end"} className="hatpl-chart-value">
                {v >= 0 ? "+" : ""}{(v * 100).toFixed(3)}%
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        5-day forward return by {label} decile, averaged per-date across {nDates.toLocaleString()} qualifying
        trading dates (min 20 names/date). This is the cross-sectional TILT across the stock universe on a given
        date -- not a backtest of buying or selling when {label} crosses a threshold on a single stock's own
        chart over time. Decile monotonicity: {mono.toFixed(4)}.
      </figcaption>
      <div className="hatpl-asof">
        Feature data as of 2026&#8209;08&#8209;12 (the stored feature history's own cutoff) &middot; cut computed
        2026&#8209;10&#8209;25 &middot; reuses {label}'s existing blessed status, no new test run.
      </div>
    </figure>
  );
}

export function TaSynthesisArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "Most of technical analysis doesn't survive testing. A few pieces of it do — and we use them. — Tickerpicker",
    description:
      "Technical analysis isn't one thing. Tested rigorously against our own cross-sectional pipeline, a handful " +
      "of indicators carry a real, modest edge -- and most of the popular ones don't. Here's exactly which is which.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Most of technical analysis doesn't survive testing. A few pieces of it do — and we use them.",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
          <a href="/">Home</a>
          <span className="mm-crumb-sep">/</span>
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — technical analysis, piece by piece</div>
        <h1>Most of technical analysis doesn't survive testing. A few pieces of it do — and we use them.</h1>

        <p>
          One thing has to be said before anything else: everything on this page tests an indicator as a
          cross-sectional RANKING factor -- does a high reading, relative to the rest of the stock universe on a
          given date, go with a higher or lower return over the next few days -- not as the discrete buy/sell
          timing rule retail trading applies to a single stock's own chart. "RSI has a modest cross-sectional
          correlation" is not the same claim as "the RSI 70/30 rule works," and nothing here should be read as
          testing the second one. Where that distinction matters for a specific indicator, we say so again,
          explicitly, in that section.
        </p>

        <p>
          With that said: technical analysis isn't one thing, and busting it wholesale would be dishonest in the
          other direction -- we run segment models that use several of these indicators as real inputs, because
          they tested real. The question that actually separates the indicators below isn't "technical vs.
          fundamental." It's whether a claim survives a pre-registered test, a realistic multiple-testing
          correction, and an honest look at stability across time. Some do. Most, tested the same way, don't.
          That's the line this page draws.
        </p>

        <blockquote className="hatpl-pullquote">
          We're not anti-technical-analysis. We're pro-rigor, and most popular technical rules don't clear the bar
          rigor sets -- a few do, and those are the ones our models actually run on.
        </blockquote>

        <h2>Already tested, already public</h2>

        <p>
          Four indicators already have their own full write-ups elsewhere on this page, with their own charts and
          numbers -- this page won't repeat them, only place them. <a href="/market-myths?evidence=fifty-two-week-high">52-week-high proximity</a> is
          a real, blessed, stable effect we use in production. <a href="/market-myths?evidence=momentum-5d">5-day momentum</a> reverses, reliably, in
          the opposite direction most people expect. <a href="/market-myths?evidence=turnover">Trading turnover</a> carries a real but fragile signal
          that mostly disappears once you split by market-cap segment. And the <a href="/market-myths?evidence=phase3-sweep">price/volume sweep</a> is
          where idiosyncratic volatility, a liquidity-cost measure, market beta, 6-month momentum, and a turnover
          spike version all tested real on their own terms and still didn't make it into a model -- each for a
          different, specific reason, not a blanket "didn't work."
        </p>

        <h2>Never shown publicly before: what's blessed, quietly, in production</h2>

        <p>
          A larger group of classic indicators have been tested through the same blessing pipeline, cleared the
          bar, and are sitting in production models right now -- without ever having their own public write-up.
          Here they are, honestly, including the ones that are blessed but weak. One precision before the list:
          the IC we cite below for each feature is our registry's stored figure — a single correlation computed
          by pooling every stock and every date into one panel at once. That is a real, legitimate statistic, and
          it's the convention this whole page otherwise uses — but it is NOT the same statistic the decile charts
          further down are built from, which bucket stocks within each date separately and average across dates.
          The two can differ meaningfully in magnitude for the same feature, as RSI and Stochastic show directly
          below; where that happens, we say so rather than let a clean chart sit next to a number it doesn't
          match.
        </p>

        <ul className="hatpl-list">
          <li><b>RSI (14-day and 5-day)</b> — both blessed. The registry's pooled IC reads −0.033 for the 5-day
            version and a much smaller −0.008 for the 14-day version — on that number alone, RSI(14) looks like
            one of the weakest features on this page. Recomputed on the per-date cross-sectional basis the decile
            chart below actually uses, both read considerably stronger and closer together — −0.046 (5-day) and
            −0.039 (14-day), t-statistics past 20 on roughly 2,470 trading dates either way. Neither version is a
            test of the 70/30 overbought/oversold rule specifically — see the decile cut below for that, and for
            the full reconciliation of these two numbers.</li>
          <li><b>Stochastic oscillator</b> (fast %K, slow %K, %D, the %K−%D spread) — all blessed, part of the
            same momentum-oscillator family as RSI and ROC. The %D line specifically is blessed but MARGINAL and
            UNSTABLE — its cross-sectional signal is indistinguishable from zero and flips sign across
            sub-periods. We don't lean on it alone. Slow %K carries the same pooled-vs-per-date gap as RSI above:
            registry IC −0.015 (pooled), recomputed per-date −0.027 (t=−16.1) — see the decile section.</li>
          <li><b>MACD</b> (line, signal, histogram) — the signal line and histogram are blessed with a stable
            sign; the raw MACD line itself is blessed but MARGINAL and UNSTABLE, the same caveat as Stochastic
            %D. We test the continuous values, not the classic "MACD crosses its signal line" timing event — that
            discrete claim is a separate, untested question (more below).</li>
          <li><b>Bollinger Bands</b> (%B and bandwidth) — both blessed, stable sign. Bandwidth was already flagged
            as one of the strongest technical signals at the INDEX level in the original V8 research, before any
            of this project's own stock-level testing began.</li>
          <li><b>Price vs. moving averages</b> (20/50-day SMA, 50-day EMA, all as a ratio to price) — all three
            blessed and stable. The 200-day version is also blessed but MARGINAL and UNSTABLE.</li>
          <li><b>The golden/death cross</b> — yes, we tested this one, as a regime state (is the 50-day average
            currently above or below the 200-day average), not as the single day it crosses. Blessed, small,
            stable positive effect: being in a "golden" regime goes with modestly higher forward returns. The
            specific crossing EVENT itself — the day-of reaction the name usually refers to — is a different,
            untested claim (more below).</li>
          <li><b>ADX</b> (trend strength) — blessed but MARGINAL and UNSTABLE, the weakest of the indicators that
            cleared the bar at all.</li>
          <li><b>ATR</b> (average true range, 7- and 14-day, as a % of price) — both blessed, stable.</li>
          <li><b>On-balance volume</b> (20-day slope) — blessed, stable.</li>
        </ul>

        <p>
          Read the "blessed" label precisely: it means a feature cleared this project's own significance and
          stability bar as a continuous ranking factor, not that it's a strong standalone signal. Several of the
          ones above are blessed but explicitly flagged MARGINAL and UNSTABLE in our own records — we carry them
          because they still clear the bar on net, not because they're clean. None of this group is remotely a
          standalone edge: the registry's pooled ICs across this page span roughly 0.001 at the weakest end (the
          MARGINAL/UNSTABLE features — the MACD line, ADX, beta-to-NIFTY, Stochastic %D) to about 0.06 at the
          strongest (ATR) — the same order of magnitude as the 52-week-high and 5-day-momentum effects already
          published here, but a wider spread than any single number would suggest. Statistically real at this
          project's sample sizes, economically modest even at the strong end, one input among many in a model
          that blends dozens of them.
        </p>

        <h2>The RSI and Stochastic 70/30 question, specifically</h2>

        <p>
          RSI's and Stochastic's blessed status above is a continuous cross-sectional correlation — not a test of
          the specific claim that stocks above 70 or below 30 behave differently from the rest. To speak to that
          directly without running a new study, we cut both already-blessed factors into deciles — the identical
          per-date cross-sectional method the 52-week-high and 5-day-momentum pages already use, reused exactly,
          not re-derived.
        </p>

        <p>
          The reconciliation promised above: recomputed directly on the SAME per-date basis these charts use,
          RSI(14)'s correlation is −0.0387 (t=−20.9, 2,467 dates) and Stochastic slow %K's is −0.0269 (t=−16.1,
          2,464 dates) — both clearly stronger than the pooled registry figures cited earlier (−0.0077 and
          −0.0150), and the actual number behind the clean, strongly monotonic shape below. The two statistics
          are legitimately different, not a contradiction: pooling blends in whatever the whole market was doing
          on each date along with the cross-sectional comparison; isolating each date first and averaging after
          removes that blending. Both are real measurements of the same feature — they just answer slightly
          different questions, and citing the smaller one next to an unexplained clean chart would have been
          misleading.
        </p>

        <DecileChart decile={RSI_DECILE} nDates={RSI_N_DATES} mono={RSI_MONO} label="RSI(14)"
                    ariaLabel="RSI(14) decile vs. 5-day forward return, per-date cross-sectional cut" />
        <DecileChart decile={STOCH_DECILE} nDates={STOCH_N_DATES} mono={STOCH_MONO} label="Stochastic slow %K(14,3)"
                    ariaLabel="Stochastic slow %K(14,3) decile vs. 5-day forward return, per-date cross-sectional cut" />

        <p>
          Read both charts as a cross-sectional tilt, not a trading backtest: on a typical date, the stocks
          sitting in the lowest decile across the whole universe tend to out-return the stocks in the highest
          decile over the next 5 days — directionally consistent with the "overbought/oversold" intuition, on
          average, across the universe, and visible in both indicators independently, with a near-perfectly
          monotonic decline across nine of the ten buckets (monotonicity &minus;0.95 for RSI, &minus;0.89 for
          Stochastic). The one wrinkle: the very top decile — the most "overbought" names — bounces back up
          slightly rather than extending the decline, the same kind of top-bucket irregularity the 52-week-high
          and 5-day-momentum pages already show and flag rather than smooth over. None of this is evidence that
          watching one stock's RSI or Stochastic cross 30 or 70 over time is a profitable timing rule — that is a
          genuinely different claim, about one stock's own history rather than the cross-section on one date, and
          neither chart speaks to it either way.
        </p>

        <h2>Beta — two different things, worth not confusing</h2>

        <p>
          Two separate features both touch "beta to the market," and they have different fates. <code>beta_252</code>
          was tested as a sweep candidate in the price/volume investigation and failed — a real raw correlation
          that added nothing once existing features already in the model were accounted for. <code>beta_nifty_252</code>
          is a different, already-blessed production feature (part of the "risk" group, alongside return
          skewness) — real, but marginal and unstable in the same way ADX and the MACD line are. They are not the
          same feature under two names; one was tried and rejected, the other is blessed-but-weak and already in
          use.
        </p>

        <h2>What we didn't test, and why</h2>

        <p>
          Two things are genuinely out of scope here, and we'd rather say so than quietly skip them. First,
          classic chart patterns — head-and-shoulders, triangles, flags — have no operational, non-discretionary
          definition in our pipeline at all; identifying one on a chart is a judgment call, not a computable rule,
          and we don't test what we can't define precisely. Second, support and resistance levels are the same
          problem in a different shape — real as a concept traders use, not reducible here to a single rule we'd
          stand behind testing. Both are excluded on principle, not quietly dropped because they failed.
        </p>

        <p>
          A narrower gap also deserves naming directly: the EVENT-level versions of two indicators above — a MACD
          line crossing its own signal line, or a 50-day average crossing a 200-day average on a specific day —
          are time-series claims about one stock's own history at one moment, a different question from the
          cross-sectional regime/continuous versions we did test. Building that properly would mean a real
          event-study (cohort construction, an entry-timing convention, its own correction for multiple testing) —
          a separate, larger piece of work, not something this synthesis page extends into.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <p className="hatpl-source" style={{ marginBottom: 10 }}>
            Methodology note: "IC" below is our feature registry's stored figure — a single POOLED rank
            correlation across the whole stock&times;date panel at once (the same convention the 52-week-high
            and 5-day-momentum cards' own "Pooled correlation (IC)" tables use). For rsi_14, rsi_5, and
            stoch_slowk_14_3 only, we additionally recomputed the PER-DATE cross-sectional correlation (bucket by
            date, average across dates) directly against live data, since those three feed the decile charts
            above, which are built on that different basis — both figures are shown for those three rows. Every
            other row below is the registry's pooled figure only, not independently re-verified against a
            per-date recompute in this pass.
          </p>
          <table>
            <tbody>
              <tr><td>rsi_5</td><td>blessed, pooled IC &minus;0.0325 (registry), stable, SEGMENT-ALIVE &middot; per-date (recomputed) &minus;0.0463, t=&minus;28.8, n=2,476</td></tr>
              <tr><td>rsi_14</td><td>blessed, pooled IC &minus;0.0077 (registry), stable &middot; per-date (recomputed, decile-chart basis) &minus;0.0387, t=&minus;20.9, n=2,467</td></tr>
              <tr><td>stoch_fastk_14_3</td><td>blessed, IC &minus;0.0212, stable, SEGMENT-ALIVE</td></tr>
              <tr><td>stoch_slowk_14_3</td><td>blessed, pooled IC &minus;0.0150 (registry), stable, SEGMENT-ALIVE &middot; per-date (recomputed, decile-chart basis) &minus;0.0269, t=&minus;16.1, n=2,464</td></tr>
              <tr><td>stoch_k_minus_d</td><td>blessed, IC &minus;0.0262, stable, SEGMENT-ALIVE</td></tr>
              <tr><td>stoch_slowd_14_3</td><td>blessed, IC &minus;0.0071, MARGINAL/UNSTABLE, SEGMENT-ALIVE</td></tr>
              <tr><td>macd_signal_9</td><td>blessed, IC +0.0113, stable</td></tr>
              <tr><td>macd_hist_12_26_9</td><td>blessed, IC &minus;0.0262, stable</td></tr>
              <tr><td>macd_line_12_26</td><td>blessed, IC +0.0010, MARGINAL/UNSTABLE</td></tr>
              <tr><td>bb_pctb_20_2</td><td>blessed, IC &minus;0.0312, stable</td></tr>
              <tr><td>bb_width_20_2</td><td>blessed, IC &minus;0.0365, stable</td></tr>
              <tr><td>price_sma20_rat</td><td>blessed, IC &minus;0.0340, stable</td></tr>
              <tr><td>price_sma50_rat</td><td>blessed, IC &minus;0.0213, stable</td></tr>
              <tr><td>price_ema50_rat</td><td>blessed, IC &minus;0.0205, stable</td></tr>
              <tr><td>price_sma200_rat</td><td>blessed, IC +0.0075, MARGINAL/UNSTABLE</td></tr>
              <tr><td>sma_50_200_regime (golden/death cross, regime form)</td><td>blessed, IC +0.0197, stable</td></tr>
              <tr><td>adx_14</td><td>blessed, IC +0.0053, MARGINAL/UNSTABLE</td></tr>
              <tr><td>atr7_pct</td><td>blessed, IC &minus;0.0628, stable</td></tr>
              <tr><td>atr14_pct</td><td>blessed, IC &minus;0.0611, stable</td></tr>
              <tr><td>obv_slope_20</td><td>blessed, IC &minus;0.0112, stable</td></tr>
              <tr><td>beta_nifty_252</td><td>blessed, IC &minus;0.0052, MARGINAL/UNSTABLE</td></tr>
              <tr><td>beta_252 (sweep candidate — different feature)</td><td>tested, rejected — raw IC &minus;0.0297, fails incremental value</td></tr>
              <tr><td>idio_vol, amihud_21, turnover_abn, mom_6m</td><td>tested, rejected — see the price/volume sweep page</td></tr>
              <tr><td>rsi_14 decile cut, H=5</td><td>n dates = {RSI_N_DATES}, monotonicity = {RSI_MONO} — see chart above</td></tr>
              <tr><td>stoch_slowk_14_3 decile cut, H=5</td><td>n dates = {STOCH_N_DATES}, monotonicity = {STOCH_MONO}</td></tr>
              <tr><td>Support/resistance, chart patterns (H&amp;S, triangles, flags)</td><td>not tested — no operational definition in this pipeline</td></tr>
              <tr><td>MACD-crossover, golden/death-cross (event-day form)</td><td>not tested — a separate time-series claim, not isolated here</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            Sources: live <code>feature_library</code> table (pulled directly, 2026-10-25) for every blessed-feature
            IC/stability verdict; <code>backend/rsi_decile_chart.py</code> &rarr; <code>data/rsi_decile_means.json</code> for
            the decile cut (reuses <code>backend/pct_52wh_decile_chart.py</code>'s machinery, generalized by feature key);
            <code>backend/rsi_ic_reconcile.py</code> for the per-date cross-sectional IC recomputation (reuses
            <code>app/derived/lab.py</code>'s own <code>cross_sectional_ic</code>/<code>rank_ic</code> functions
            unmodified, run directly against live data); the four linked cards for everything already published
            there.
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
